import {
  Typography,
  Box,
  Grid,
  TextField,
  useTheme,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React, { useState } from "react";
import TreffasButton from "src/components/TreffasButton";
import TreffasStyle from "src/utils/TreffasStyle";
import { Controller as Fc } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Square } from "react-feather";
import { grey, red } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import {
  CheckBox as CheckBoxIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const BasicInfo = ({
  control,
  errors,
  trigger,
  terms,
  setTerms,
  submitBtnRef,
  isProcess,
}) => {
  const themes = useTheme();
  const navigate = useNavigate();
  const [termError, setTermError] = useState(false);
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const handleSignup = async () => {
    let error = [];
    const noError = await trigger(["firstname", "lastname", "password"]);

    if (!noError) {
      error = "fields-required";
    }

    if (!terms) {
      error = "terms-required";
      setTermError(true);
    }

    if (error.length === 0) {
      submitBtnRef?.current.click();
    }
  };

  return (
    <>
      <Box>
        <Grid container spacing={"16px"}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                sx={{
                  ...TreffasStyle.formLabel,
                }}
              >
                {t("registration.form.labels.firstname")}
              </Typography>

              <Fc
                name="firstname"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Enter first name"
                    type="text"
                    inputProps={{ "data-testid": "signup-firstname-input" }}
                    sx={TreffasStyle.formInput(themes)}
                    error={Boolean(errors.firstname)}
                    helperText={errors.firstname?.message}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                sx={{
                  ...TreffasStyle.formLabel,
                }}
              >
                {t("registration.form.labels.lastname")}
              </Typography>

              <Fc
                name="lastname"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Enter last name"
                    type="text"
                    inputProps={{ "data-testid": "signup-lastname-input" }}
                    sx={TreffasStyle.formInput(themes)}
                    error={Boolean(errors.lastname)}
                    helperText={errors.lastname?.message}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box>
              <Typography
                sx={{
                  ...TreffasStyle.formLabel,
                }}
              >
                {t("registration.form.labels.email")}
              </Typography>

              <Fc
                name="email"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="e.g. bettycooper@gmail.com"
                    type="email"
                    inputProps={{ "data-testid": "signup-email-input" }}
                    sx={TreffasStyle.formInput(themes)}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <Box>
              <Typography
                sx={{
                  ...TreffasStyle.formLabel,
                }}
              >
                {t("registration.form.labels.password")}
              </Typography>

              <Fc
                name="password"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Enter password"
                    type={showPassword ? "text" : "password"}
                    inputProps={{ "data-testid": "signup-password-input" }}
                    sx={TreffasStyle.formInput(themes)}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {!showPassword ? (
                              <Visibility sx={{ fontSize: "18px" }} />
                            ) : (
                              <VisibilityOff sx={{ fontSize: "18px" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: "24px" }}>
        <FormControlLabel
          control={
            <Checkbox
              inputProps={{ "data-testid": "signup-terms-checkbox" }}
              checked={terms}
              onChange={() => setTerms(!terms)}
              icon={<Square color={termError ? red[600] : grey[400]} />}
              checkedIcon={<CheckBoxIcon sx={{ fill: "var(--color-brand-primary)" }} />}
            />
          }
          label={
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "12px",
                textAlign: "left",
                color: "#000",
              }}
            >
              {t("registration.form.labels.agreement.first")}
              <Typography
                variant="subtitle"
                sx={{
                  cursor: "pointer",
                  marginLeft: 0.5,
                  marginRight: 0.5,
                  fontWeight: 500,
                  color: "var(--color-brand-primary)",
                }}
              >
                {t("registration.form.labels.agreement.second")}
              </Typography>
              {t("registration.form.labels.agreement.third")}
              <Typography
                variant="subtitle"
                sx={{
                  cursor: "pointer",
                  marginLeft: 0.5,
                  marginRight: 0.5,
                  fontWeight: 500,
                  color: "var(--color-brand-primary)",
                }}
              >
                {t("registration.form.labels.agreement.fourth")}
              </Typography>
              .
            </Typography>
          }
        />
      </Box>

      <Box sx={{ mt: "24px" }}>
        <TreffasButton
          variant="primary"
          size="md"
          fullWidth
          data-testid="signup-submit-btn"
          sx={{ borderRadius: "8px", textTransform: "inherit", fontSize: "14px" }}
          onClick={() => handleSignup()}
          loading={isProcess}
          disabled={isProcess}
        >
          {t("registration.form.button.signup")}
        </TreffasButton>
      </Box>

      <Box
        sx={(themes) => ({
          mt: "24px",
          pb: "24px",
        })}
      >
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "24px",
            textAlign: "center",
            color: "#000",
            fontFamily: "var(--font-work-sans)",
          }}
        >
          {t("registration.form.button.no-account.first")}
          <Typography
            variant="subtitle"
            sx={{
              cursor: "pointer",
              marginLeft: 0.5,
              marginRight: 0.5,
              fontWeight: 500,
              color: "var(--color-brand-primary)",
              fontFamily: "var(--font-work-sans)",
            }}
            onClick={() => navigate("/login")}
          >
            {t("registration.form.button.no-account.second")}
          </Typography>
          {t("registration.form.button.no-account.third")}
        </Typography>
      </Box>
    </>
  );
};

export default BasicInfo;
